import { useMemo } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  Card,
  CardContent,
  CardFooter,
  Chip,
  Division,
  Flex,
  Heading,
  Icons,
  Paragraph,
  Skeleton,
  Span,
} from "@ir/ui";
import { useAnalyticsKPI, useAnalyticsMobile } from "@/hooks";
import { ANALYTICS_CHECK_DATE_RANGE_LABEL } from "@/store/analytics";
import { IDisputePipelinePurchaserItem } from "@/core/repositories/analytics";
import EmptyKpiCard from "./EmptyKpiCard";
import "../../styles/Analytics.page.scss";

const PENDING_FILL = "var(--warning-400)";
const SUBMITTED_FILL = "var(--secondary-400)";
const PENDING_COUNT_COLOR = "var(--warning-600)";
const SUBMITTED_COUNT_COLOR = "var(--secondary-600)";

const HEADING = "Dispute Pipeline";

type ChartItem = {
  purchaserName: string;
  pendingAmount: number;
  submittedAmount: number;
  pendingCount: number;
  submittedCount: number;
};

type SegmentKey = "pendingAmount" | "submittedAmount";

const formatCompactCurrency = (value: number) =>
  new Intl.NumberFormat("en-US", {
    notation: "compact",
    compactDisplay: "short",
    maximumFractionDigits: 0,
  })
    .format(value)
    .replace(/^/, "$");

const formatCurrencyFull = (value: number) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(value);

const formatAxisK = (value: number) => `$${Math.round(value / 1000)}K`;

const getPurchaserLabel = (item: IDisputePipelinePurchaserItem) =>
  item.isOthers ? "Others" : item.purchaser.purchaserName;

const getNiceAxisMax = (maxValue: number): number => {
  if (maxValue <= 0) return 100_000;

  const steps = [10_000, 25_000, 50_000, 100_000, 250_000, 500_000, 1_000_000];
  const step = steps.find(s => maxValue / s <= 5) ?? 1_000_000;
  return Math.ceil(maxValue / step) * step;
};

const mapPurchasersToChartData = (purchasers: IDisputePipelinePurchaserItem[]): ChartItem[] =>
  purchasers.map(item => ({
    purchaserName: getPurchaserLabel(item),
    pendingAmount: item.pending.amount,
    submittedAmount: item.submitted.amount,
    pendingCount: item.pending.count,
    submittedCount: item.submitted.count,
  }));

const DisputePipelineTooltip = ({
  active,
  payload,
}: {
  active?: boolean;
  payload?: { dataKey: SegmentKey; payload: ChartItem }[];
}) => {
  if (!active || !payload?.length) return null;

  const item = payload[0].payload;
  const hoveredSegment = payload[0].dataKey;

  const getRowStyle = (segment: SegmentKey) => ({
    marginBottom: 4,
    color: hoveredSegment === segment ? "var(--white-color)" : "var(--grey-300)",
    fontWeight: hoveredSegment === segment ? 600 : 400,
  });

  return (
    <Division
      style={{
        background: "var(--grey-900)",
        borderRadius: 8,
        padding: "10px 14px",
        color: "var(--white-color)",
        fontSize: 13,
        minWidth: 200,
      }}
    >
      <Division style={{ fontWeight: 600, marginBottom: 8 }}>{item.purchaserName}</Division>
      <Division style={getRowStyle("pendingAmount")}>
        Pending: {formatCurrencyFull(item.pendingAmount)} ({item.pendingCount.toLocaleString()} deductions)
      </Division>
      <Division style={getRowStyle("submittedAmount")}>
        Submitted: {formatCurrencyFull(item.submittedAmount)} ({item.submittedCount.toLocaleString()} deductions)
      </Division>
    </Division>
  );
};

const SummaryStat = ({
  label,
  count,
  amount,
  accentColor,
  countColor,
}: {
  label: string;
  count: number;
  amount: number;
  accentColor: string;
  countColor: string;
}) => (
  <Flex
    align="stretch"
    style={{
      flex: 1,
      borderRadius: 8,
      overflow: "hidden",
    }}
    className="bg-grey-10 border-1 border-color-grey-25"
  >
    <Division style={{ width: 5, backgroundColor: accentColor, flexShrink: 0 }} />
    <Flex direction="column" style={{ padding: "12px 16px" }} gap={0.25}>
      <Span className="text-md font-bold text-grey-500" style={{ fontSize: 13 }}>
        {label}
      </Span>
      <Span style={{ fontSize: 28, fontWeight: 700, lineHeight: 1.1, color: countColor }}>
        {count.toLocaleString()}
      </Span>
      <Span className="text-grey-500" style={{ fontSize: 16 }}>
        {formatCompactCurrency(amount)}
      </Span>
    </Flex>
  </Flex>
);

const CHART_SKELETON_BARS = [
  { key: "bar-1", height: 120 },
  { key: "bar-2", height: 160 },
  { key: "bar-3", height: 90 },
  { key: "bar-4", height: 140 },
  { key: "bar-5", height: 100 },
  { key: "bar-6", height: 70 },
] as const;

const DisputePipelineSkeleton = () => (
  <Division style={{ padding: "var(--spacing-3)" }}>
    <Flex gap={2} style={{ marginBottom: 24 }}>
      <Skeleton height={88} style={{ flex: 1, borderRadius: 8 }} />
      <Skeleton height={88} style={{ flex: 1, borderRadius: 8 }} />
    </Flex>
    <Skeleton width={160} height={12} style={{ borderRadius: 4, marginBottom: 16 }} />
    <Flex align="end" justifyContent="space-between" gap={1} style={{ height: 200 }}>
      {CHART_SKELETON_BARS.map(bar => (
        <Skeleton key={bar.key} width={40} height={bar.height} style={{ borderRadius: 4 }} />
      ))}
    </Flex>
  </Division>
);

const DisputePipelineChart = ({ chartData }: { chartData: ChartItem[] }) => {
  const yAxisMax = useMemo(() => {
    const maxTotal = Math.max(...chartData.map(d => d.pendingAmount + d.submittedAmount), 0);
    return getNiceAxisMax(maxTotal);
  }, [chartData]);

  return (
    <Division>
      <ResponsiveContainer width="100%" height={200}>
        <BarChart
          data={chartData}
          margin={{ top: 8, right: 12, left: 4, bottom: 4 }}
        >
          <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="var(--grey-200)" />
          <XAxis
            dataKey="purchaserName"
            stroke="var(--grey-500)"
            axisLine={false}
            tickLine={false}
            tick={{ fontSize: 12 }}
            interval={0}
            angle={chartData.length > 5 ? -25 : 0}
            textAnchor={chartData.length > 5 ? "end" : "middle"}
            height={chartData.length > 5 ? 56 : 32}
          />
          <YAxis
            stroke="var(--grey-500)"
            axisLine={false}
            tickLine={false}
            tickFormatter={formatAxisK}
            domain={[0, yAxisMax]}
            tick={{ fontSize: 12 }}
          />
          <Tooltip content={<DisputePipelineTooltip />} cursor={{ fill: "var(--grey-50)" }} />
          <Legend
            verticalAlign="bottom"
            height={36}
            iconType="square"
            iconSize={10}
            formatter={value => (value === "pendingAmount" ? "Pending ($K)" : "Submitted ($K)")}
          />
          <Bar dataKey="pendingAmount" name="pendingAmount" stackId="dispute" fill={PENDING_FILL} />
          <Bar
            dataKey="submittedAmount"
            name="submittedAmount"
            stackId="dispute"
            fill={SUBMITTED_FILL}
            radius={[4, 4, 0, 0]}
          />
        </BarChart>
      </ResponsiveContainer>
    </Division>
  );
};

const DisputePipelineCard = () => {
  const { disputePipeline } = useAnalyticsKPI();
  const isMobile = useAnalyticsMobile();
  const chartData = useMemo(
    () => (disputePipeline.data ? mapPurchasersToChartData(disputePipeline.data.purchasers) : []),
    [disputePipeline.data]
  );

  const hasPurchasers = chartData.length > 0;

  const cardContent = (() => {
    if (disputePipeline.isLoading) {
      return <DisputePipelineSkeleton />;
    }

    if (!disputePipeline.data || !hasPurchasers) {
      return (
        <Flex justifyContent="center" align="center" style={{ height: 350 }}>
          <EmptyKpiCard
            title="No data available"
            description="No dispute pipeline data for the selected period. Try adjusting the date range."
            icon={Icons.BarChart5}
          />
        </Flex>
      );
    }

    const { summary } = disputePipeline.data;

    return (
      <Division style={{ padding: "var(--spacing-3)" }}>
        <Flex gap={1} className="analytics-dispute-summary" style={{ marginBottom: 24 }}>
          <SummaryStat
            label="Pending"
            count={summary.pending.count}
            amount={summary.pending.amount}
            accentColor={PENDING_FILL}
            countColor={PENDING_COUNT_COLOR}
          />
          <SummaryStat
            label="Submitted"
            count={summary.submitted.count}
            amount={summary.submitted.amount}
            accentColor={SUBMITTED_FILL}
            countColor={SUBMITTED_COUNT_COLOR}
          />
        </Flex>
        <DisputePipelineChart chartData={chartData} />
      </Division>
    );
  })();

  return (
    <Card className="bg-white border-1 border-color-grey-50">
      <Flex className="px-4 py-3 border-bottom-1 border-color-grey-25" direction="column">
        <Heading text={HEADING} level={6} className="m-0 text-grey-500 font-bold" />
        {!isMobile && (
          <Paragraph className="m-0 text-grey-400">Pending + Submitted</Paragraph>
        )}
      </Flex>
      <CardContent style={{ minHeight: !isMobile ? 370 : "auto" }}>{cardContent}</CardContent>
      <CardFooter
        className="analytics-card-footer bg-grey-10 text-grey-500"
        style={{ borderBottomLeftRadius: ".75rem", borderBottomRightRadius: ".75rem" }}
      >
        <Flex gap={0.5} align="center" wrap="wrap">
          Filter applied:{" "}
          <Chip label={ANALYTICS_CHECK_DATE_RANGE_LABEL} variant="filled" color="secondary" borderless />
        </Flex>
      </CardFooter>
    </Card>
  );
};

export default DisputePipelineCard;
